import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listEntities } from '@/api/entities';
import { getValidationBoard } from '@/api/cashflows';
import Table, { type Column } from '@/components/ui/Table';
import IconButton from '@/components/ui/IconButton';
import { Eye } from 'lucide-react';
import type { Entity, ValidationCell } from '@/types';
import { formatPeriod, generateRecentPeriods } from '@/utils/format';

function cellDot(cell: ValidationCell | undefined) {
  if (!cell) return <span className="inline-block h-3 w-3 rounded-full bg-gray-200" title="No data" />;
  if (cell.cellStatus === 'AGREED' && cell.isBalanced && cell.cf9999IsZero) {
    return <span className="inline-block h-3 w-3 rounded-full bg-green-500" title="Agreed & balanced" />;
  }
  if (cell.cellStatus === 'SUBMITTED') {
    return <span className="inline-block h-3 w-3 rounded-full bg-amber-400" title="Submitted" />;
  }
  if (cell.cellStatus === 'IN_PROGRESS') {
    return <span className="inline-block h-3 w-3 rounded-full bg-blue-400" title="In progress" />;
  }
  if (cell.cellStatus === 'NOT_STARTED') {
    return <span className="inline-block h-3 w-3 rounded-full bg-gray-200" title="Not started" />;
  }
  if (!cell.isBalanced || !cell.cf9999IsZero) {
    return <span className="inline-block h-3 w-3 rounded-full bg-red-500" title="Unbalanced" />;
  }
  return <span className="inline-block h-3 w-3 rounded-full bg-gray-200" title="Not started" />;
}

const STATUS_ORDER: Record<string, number> = {
  NOT_STARTED: 0,
  IN_PROGRESS: 1,
  SUBMITTED: 2,
  AGREED: 3,
};

export default function EntityListPage() {
  const navigate = useNavigate();
  const recentPeriods = generateRecentPeriods(18);
  const [selectedPeriod, setSelectedPeriod] = useState(recentPeriods[0]);
  const [query, setQuery] = useState('');

  const { data: entities, isLoading: entitiesLoading } = useQuery({
    queryKey: ['entities'],
    queryFn: listEntities,
  });

  const { data: board, isLoading: boardLoading } = useQuery({
    queryKey: ['validation-board', selectedPeriod],
    queryFn: () => getValidationBoard(selectedPeriod),
    // Keep colours live: other users submit/agree from their own browsers, so
    // poll and refetch on focus rather than requiring a manual page refresh.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  // Build a map: entityId → ValidationEntityRow
  const entityCellMap = new Map<string, Record<string, ValidationCell>>();
  if (board) {
    for (const div of board.divisions) {
      for (const ent of div.entities) {
        entityCellMap.set(ent.entity.id, ent.cells);
      }
    }
  }

  const q = query.trim().toLowerCase();
  const filteredEntities = (entities ?? []).filter((e) => {
    if (!q) return true;
    return (
      e.name.toLowerCase().includes(q) ||
      e.code.toLowerCase().includes(q) ||
      (e.division?.name ?? '').toLowerCase().includes(q)
    );
  });

  const columns: Column<Entity>[] = [
    {
      key: 'name',
      header: 'Entity',
      sortable: true,
      sortValue: (e) => e.name,
      render: (e) => (
        <div>
          <span className="font-medium text-gray-900">{e.name}</span>
          <span className="ml-2 text-xs text-gray-600">{e.code}</span>
        </div>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      sortValue: (e) => e.code,
      render: (e) => <span className="font-mono text-xs text-gray-600">{e.code}</span>,
    },
    {
      key: 'division',
      header: 'Division',
      sortable: true,
      sortValue: (e) => e.division?.name ?? '',
      render: (e) => <span className="text-gray-600">{e.division?.name ?? '—'}</span>,
    },
    {
      key: 'current',
      header: formatPeriod(selectedPeriod),
      sortable: true,
      sortValue: (e) => {
        const cell = entityCellMap.get(e.id)?.[selectedPeriod];
        return STATUS_ORDER[cell?.cellStatus ?? ''] ?? -1;
      },
      render: (e) => {
        const cells = entityCellMap.get(e.id);
        const cell = cells?.[selectedPeriod];
        return (
          <div className="flex items-center gap-2">
            {cellDot(cell)}
            <span className="text-xs text-gray-500">
              {cell?.cellStatus?.replace('_', ' ') ?? 'No data'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'action',
      header: '',
      render: (e) => (
        <IconButton
          icon={Eye}
          label="Enter / view cash flow"
          variant="primary"
          onClick={(ev) => {
            ev.stopPropagation();
            navigate(`/entities/${e.id}/cashflow/${selectedPeriod}`);
          }}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Entities</h1>
          <p className="mt-1 text-sm text-gray-500">
            Select an entity to enter or review its cash flow.
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            Entity and division setup is managed in the month-end close app and synced here hourly.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
            aria-label="Filter by period"
          >
            {recentPeriods.map((p) => (
              <option key={p} value={p}>{formatPeriod(p)}</option>
            ))}
          </select>
          <div className="relative w-56">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
            >
              <circle cx="10.5" cy="10.5" r="6.75" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75L21 21" />
            </svg>
            <input
              type="search"
              placeholder="Search entities…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={filteredEntities}
        loading={entitiesLoading || boardLoading}
        rowKey={(e) => e.id}
        onRowClick={(e) => navigate(`/entities/${e.id}/cashflow/${selectedPeriod}`)}
        emptyMessage={q ? `No entities match "${query}".` : 'No entities found.'}
      />
    </div>
  );
}
